import { useEffect, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import { io, Socket } from 'socket.io-client';

export interface TokenPriceUpdatePayload {
  last: number;
  high24h?: number;
  low24h?: number;
  change?: number;
  percentage?: number;
}

export interface UseTokenPriceResult {
  price: number | null;
  updatedAt: string | null;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
}

/**
 * Generic socket-driven token price subscriber.
 *
 * Backend emits `${currency}PriceUpdate` (e.g. `polPriceUpdate`,
 * `solPriceUpdate`). This hook opens a dedicated socket connection,
 * listens to one event, and exposes the latest price.
 */
export const useTokenPrice = (eventName: string): UseTokenPriceResult => {
  const [price, setPrice] = useState<number | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  const [error, setError] = useState<unknown>(null);

  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    const endpoint = process.env.REACT_APP_SOCKET_ENDPOINT;
    const token = localStorage.getItem('jwt') ?? Cookies.get('jwt');

    if (!endpoint) {
      setIsError(true);
      setError(new Error('REACT_APP_SOCKET_ENDPOINT is not defined'));
      setIsLoading(false);
      return;
    }

    const socket = io(endpoint, {
      transports: ['websocket'],
      withCredentials: true,
      query: token ? { token } : undefined,
    });

    socketRef.current = socket;

    const handlePriceUpdate = (data: TokenPriceUpdatePayload) => {
      if (typeof data?.last === 'number') {
        setPrice(data.last);
        setUpdatedAt(new Date().toISOString());
        setIsLoading(false);
        setIsError(false);
        setError(null);
      }
    };

    const handleConnectError = (err: unknown) => {
      setIsError(true);
      setError(err);
      setIsLoading(false);
    };

    socket.on(eventName, handlePriceUpdate);
    socket.on('connect_error', handleConnectError);

    return () => {
      socket.off(eventName, handlePriceUpdate);
      socket.off('connect_error', handleConnectError);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [eventName]);

  const refetch = () => {
    setIsLoading(true);
  };

  return {
    price,
    updatedAt,
    isLoading,
    isError,
    error,
    refetch,
  };
};
